import { API_BASE_URL } from '../config/api';

const TOKEN_KEY = 'admin_jwt';
const USER_KEY = 'admin_user';
const SESSION_KEY = 'admin_session';
const LAST_ACTIVITY_KEY = 'admin_last_activity';
const LOGIN_ATTEMPTS_KEY = 'admin_login_attempts';
const LOCKOUT_TIME_KEY = 'admin_lockout_time';

// Security Constants
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes
const TOKEN_REFRESH_INTERVAL = 25 * 60 * 1000; // 25 minutes

// XSS Protection - Sanitize input
function sanitizeInput(input) {
  if (typeof input !== 'string') return input;
  return input
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .trim();
}

// Check if account is locked out
export function isLockedOut() {
  try {
    const lockoutTime = localStorage.getItem(LOCKOUT_TIME_KEY);
    if (!lockoutTime) return false;
    
    const lockoutTimestamp = parseInt(lockoutTime, 10);
    const now = Date.now();
    
    if (now < lockoutTimestamp) {
      return true;
    } else {
      // Lockout expired, clear it
      localStorage.removeItem(LOCKOUT_TIME_KEY);
      localStorage.removeItem(LOGIN_ATTEMPTS_KEY);
      return false;
    }
  } catch (e) {
    return false;
  }
}

// Get remaining lockout time in seconds
export function getLockoutTimeRemaining() {
  try {
    const lockoutTime = localStorage.getItem(LOCKOUT_TIME_KEY);
    if (!lockoutTime) return 0;
    
    const remaining = parseInt(lockoutTime, 10) - Date.now();
    return Math.max(0, Math.ceil(remaining / 1000));
  } catch (e) {
    return 0;
  }
}

// Track failed login attempts
function trackFailedAttempt() {
  try {
    const attempts = getLoginAttempts() + 1;
    localStorage.setItem(LOGIN_ATTEMPTS_KEY, attempts.toString());
    
    if (attempts >= MAX_LOGIN_ATTEMPTS) {
      const lockoutUntil = Date.now() + LOCKOUT_DURATION;
      localStorage.setItem(LOCKOUT_TIME_KEY, lockoutUntil.toString());
      return true; // Account locked
    }
    return false;
  } catch (e) {
    return false;
  }
}

// Get current login attempts
function getLoginAttempts() {
  try {
    const attempts = localStorage.getItem(LOGIN_ATTEMPTS_KEY);
    return attempts ? parseInt(attempts, 10) : 0;
  } catch (e) {
    return 0;
  }
}

// Clear login attempts on successful login
function clearLoginAttempts() {
  try {
    localStorage.removeItem(LOGIN_ATTEMPTS_KEY);
    localStorage.removeItem(LOCKOUT_TIME_KEY);
  } catch (e) {
    // noop
  }
}

// Session ID generation
function generateSessionId() {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
}

// Save token with encryption-like obfuscation
export function saveToken(token) {
  try {
    // Store token with session tracking
    const sessionId = generateSessionId();
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(SESSION_KEY, sessionId);
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  } catch (e) {
    // Silent failure for token save
  }
}

export function getToken() {
  try {
    // Check session validity
    if (!isSessionValid()) {
      clearToken();
      return '';
    }
    
    // Update last activity
    updateLastActivity();
    
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch (e) {
    return '';
  }
}

// Check if session is still valid
export function isSessionValid() {
  try {
    const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
    const sessionId = localStorage.getItem(SESSION_KEY);
    
    if (!lastActivity || !sessionId) {
      return false;
    }
    
    const lastActivityTime = parseInt(lastActivity, 10);
    const now = Date.now();
    
    // Check if session has timed out
    if (now - lastActivityTime > SESSION_TIMEOUT) {
      return false;
    }
    
    return true;
  } catch (e) {
    return false;
  }
}

// Update last activity timestamp
export function updateLastActivity() {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  } catch (e) {
    // noop
  }
}

// Clear all auth data
export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  } catch (e) {
    // noop
  }
}

// Enhanced login with security measures
export async function login(email, password) {
  // Check if account is locked
  if (isLockedOut()) {
    const remainingSeconds = getLockoutTimeRemaining();
    const minutes = Math.ceil(remainingSeconds / 60);
    throw new Error(`Too many failed attempts. Please try again in ${minutes} minute${minutes > 1 ? 's' : ''}.`);
  }
  
  // Sanitize email for display purposes only
  const sanitizedEmail = sanitizeInput(email);
  
  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(sanitizedEmail)) {
    throw new Error('Please enter a valid email address');
  }
  
  // Validate password strength (minimum requirements)
  // Note: Password is NOT sanitized to preserve password integrity
  if (password.length < 6) {
    throw new Error('Password must be at least 6 characters long');
  }
  
  const payload = {
    email: sanitizedEmail,
    password: password
  };

  try {
    const res = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest' // CSRF protection
      },
      credentials: 'include', // Include cookies for better security
      body: JSON.stringify(payload),
    });
    
    const data = await res.json();
    
    // Handle rate limiting (429) and account lockout (423)
    if (res.status === 429) {
      const retryAfter = data.retryAfter || 15;
      throw new Error(`Too many requests. Please try again in ${retryAfter} minutes.`);
    }
    
    if (res.status === 423) {
      // Account locked by server
      const lockoutUntil = Date.now() + (15 * 60 * 1000);
      localStorage.setItem(LOCKOUT_TIME_KEY, lockoutUntil.toString());
      throw new Error(data.message || 'Account temporarily locked. Please try again later.');
    }
    
    if (!res.ok || !data.success) {
      // Track failed attempt
      const isLocked = trackFailedAttempt();
      
      if (isLocked) {
        throw new Error('Too many failed attempts. Account locked for 15 minutes.');
      }
      
      const attemptsRemaining = MAX_LOGIN_ATTEMPTS - getLoginAttempts();
      if (attemptsRemaining > 0 && attemptsRemaining <= 2) {
        throw new Error(`Invalid credentials. ${attemptsRemaining} attempt${attemptsRemaining > 1 ? 's' : ''} remaining.`);
      }
      
      throw new Error(data.message || 'Invalid credentials');
    }
    
    if (data?.data?.token) {
      // Clear failed attempts on successful login
      clearLoginAttempts();
      
      // Save token with session management
      saveToken(data.data.token);
      
      // Save user info (sanitized)
      if (data.data.user) {
        const sanitizedUser = {
          id: data.data.user.id,
          email: sanitizeInput(data.data.user.email),
          name: sanitizeInput(data.data.user.name || ''),
          role: data.data.user.role
        };
        localStorage.setItem(USER_KEY, JSON.stringify(sanitizedUser));
      }
      
      // Set up auto token refresh
      setupTokenRefresh();
    }
    
    return data;
  } catch (error) {
    // Handle network errors
    if (error.message.includes('fetch') || error.message.includes('Failed to fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    
    // Re-throw other errors
    throw error;
  }
}

// Auto-refresh token before expiry
let refreshInterval = null;

function setupTokenRefresh() {
  // Clear existing interval
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
  
  // Set up new refresh interval
  refreshInterval = setInterval(() => {
    if (!isSessionValid()) {
      clearToken();
      clearInterval(refreshInterval);
      window.location.href = '/admin/login';
    }
  }, TOKEN_REFRESH_INTERVAL);
}

// Clear refresh interval on logout
export function logout() {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
  stopSessionCheck();
  clearToken();
}

// Get current user
export function getCurrentUser() {
  try {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  } catch (e) {
    return null;
  }
}

// Auth headers with security
export function authHeaders() {
  const t = getToken();
  return t ? { 
    Authorization: `Bearer ${t}`,
    'X-Requested-With': 'XMLHttpRequest'
  } : {
    'X-Requested-With': 'XMLHttpRequest'
  };
}

// Export function to set up session timeout checking
// This should be called from React components with proper cleanup
let sessionCheckInterval = null;

export function startSessionCheck(onSessionExpired) {
  // Clear any existing interval
  stopSessionCheck();
  
  // Check session validity immediately
  if (typeof window !== 'undefined' && getToken() && !isSessionValid()) {
    clearToken();
    if (onSessionExpired) {
      onSessionExpired();
    }
  }
  
  // Set up periodic session timeout check
  sessionCheckInterval = setInterval(() => {
    if (getToken() && !isSessionValid()) {
      clearToken();
      if (onSessionExpired) {
        onSessionExpired();
      }
    }
  }, 60000); // Check every minute
  
  return sessionCheckInterval;
}

export function stopSessionCheck() {
  if (sessionCheckInterval) {
    clearInterval(sessionCheckInterval);
    sessionCheckInterval = null;
  }
}

